import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Cross } from "@styled-icons/entypo/Cross";
import useWindowSize from "layout/useWindowSize";
import rcrmActions from "modules/rcrm/actions";
import rcrmSelectors from "modules/rcrm/selectors";
import googleActions from "modules/google/actions";
import googleSelectors from "modules/google/selectors";
import Modal from "components/Modal";
import Column from "components/Column";
import Row from "components/Row";
import Button from "components/Buttons";
import Icon from "components/Icon";
import GoogleAdsSettingContent from "components/gads/GoogleAdsSettingContent";
import GoogleSettingContent from "components/gads/GoogleSettingContent";
import CRMSettingContent from "components/CRMSettingContent";
import Toast from "components/Toast";

const SettingModal = ({ show, setShow, status, currentTab }) => {
  const rmpToken = sessionStorage.getItem("rmp_token");
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const windowSize = useWindowSize();
  const setCheckedGoogleAdsFormItemsResult = useSelector((state) =>
    googleSelectors.setCheckedGoogleAdsFormItemsResult(state),
  );
  const changeGoogleAdsFormSelectTypeResult = useSelector((state) =>
    googleSelectors.changeGoogleAdsFormSelectTypeResult(state),
  );
  const changeSheetsUrlInputResult = useSelector((state) =>
    googleSelectors.changeGoogleLeadSheetsUrlInputResult(state),
  );
  const googleUserInfoResult = useSelector((state) => googleSelectors.getGoogleExchangeTokenResult(state));
  const googleLeadFormSettingResult = useSelector((state) => googleSelectors.getGoogleLeadFormSettingResult(state));
  const googleSettingResult = useSelector((state) => googleSelectors.getGoogleSheetsSettingResult(state));
  const rcrmGateItem = useSelector((state) => rcrmSelectors.setGateItemResult(state));
  const checkGoogleSheetsError = useSelector((state) => googleSelectors.setValidateGGLeadsGoogleSheetsUrlResult(state));
  const googleAdsLoading = useSelector((state) => googleSelectors.setGoogleAdsLoadingResult(state));
  const googleLoading = useSelector((state) => googleSelectors.setGoogleLoadingResult(state));
  const googleSheetsLoading = useSelector((state) => googleSelectors.setGoogleSheetsLoadingResult(state));
  const rcrmLoading = useSelector((state) => rcrmSelectors.setCRMLoadingResult(state));
  const [statusLoading, setStatusLoading] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showFailedToast, setShowFailedToast] = useState(false);

  /**
   * googleAdsLoading.data.loading = false if the getGoogleAdsLeadForm has finished loading
   * googleLoading.data.loading = false if the getGoogleSetting has finished loading
   * googleSheetsLoading.data.loading = false if the getGoogleSheetsList has finished loading
   */
  const checkGSheetsDataLoadingStatus =
    googleAdsLoading.data.loading || googleLoading.data.loading || googleSheetsLoading.data.loading;

  /**
   * googleAdsLoading.data.loading = false if the getGoogleAdsLeadForm has finished loading
   * rcrmLoading.data.loading = false if the getCRMSetting has finished loading
   */
  const checkRCRMDataLoadingStatus = googleAdsLoading.data.loading || rcrmLoading.data.loading;

  /**
   * reset state value after close modal
   */
  const resetLeadFormSetting = async () => {
    await dispatch(googleActions.setGoogleLeadGoogleConnected(false));
    await dispatch(googleActions.setGoogleLeadGoogleDisconnected(false));
    await dispatch(
      googleActions.getGoogleExchangeToken({
        token: rmpToken,
        code: null,
      }),
    );
    await dispatch(
      googleActions.getGoogleSheetsSetting({
        token: rmpToken,
        id: null,
      }),
    );
    await dispatch(googleActions.setGoogleSheetsError(false));
    setStatusLoading(false);
  };

  /** close modal */
  const handleSettingCloseModal = async (currentTab) => {
    setShow("hide");
    await dispatch(googleActions.setValidateGoogleLeadForm({ result: false }));
    if (currentTab === "googleSheets") {
      await resetLeadFormSetting();
    }
  };

  const getAllLeadFormSettings = async (tab) => {
    await dispatch(
      googleActions.getAllGoogleLeadFormSettings({
        token: rmpToken,
        appReserve: tab,
      }),
    );
  };

  const getGoogleAdsLeadForm = async (tab) => {
    dispatch(
      googleActions.getGoogleAdsLeadForm({
        token: rmpToken,
        appReserve: tab,
      }),
    );
  };

  /** save google account to database (google_account table) */
  const setGoogleAccount = async () => {
    await dispatch(
      googleActions.setGoogleAccount({
        token: rmpToken,
        tokens: googleUserInfoResult.result === 0 ? googleUserInfoResult.data.tokens : null,
      }),
    );
  };

  /**
   * save connection (Google Sheets)
   * @param {*} params
   */
  const setLeadFormSetting = async (params) => {
    const leadFormSetting = await dispatch(
      googleActions.setGoogleAdsLeadFormSetting({
        token: rmpToken,
        appReserve: "googleSheets",
        settingId:
          googleLeadFormSettingResult.result === 0 && googleLeadFormSettingResult.msg === "GET_DATA_COMPLETED"
            ? googleLeadFormSettingResult.data.id
            : "0",
        formSetting: params.formSetting,
        assetId: params.assetId,
      }),
    );

    /**
     * if duplicate form will show error in modal and can't save
     * if can't save facebook data (left side) will save only google data (right side)
     * if can't save google data (right side) will save only facebook data (left side)
     * if can't save both data will show failed toast
     */
    if (leadFormSetting.result !== 0) {
      /** save same form on same time */
      if (
        leadFormSetting.duplicateForm != undefined &&
        leadFormSetting.duplicateForm.length > 0 &&
        leadFormSetting.msg === "DUPLICATE_FORM_SELECTED"
      ) {
        if (params.formSetting == "all") {
          await dispatch(
            googleActions.getGoogleAdsLeadForm({
              token: rmpToken,
              appReserve: "googleSheets",
            }),
          );
          await dispatch(googleActions.setDuplicateGoogleForm({ duplicate: "all" }));
          await dispatch(googleActions.setValidateGoogleLeadForm({ result: true, msg: "least_one_form" }));
        } else if (params.formSetting == "select") {
          const result = setCheckedGoogleAdsFormItemsResult.data.items.filter(
            (item) => !leadFormSetting.duplicateForm.includes(item),
          );
          await dispatch(
            googleActions.getGoogleAdsLeadForm({
              token: rmpToken,
              appReserve: "googleSheets",
            }),
          );
          await dispatch(googleActions.setCheckedGoogleAdsFormItems(result));
          await dispatch(googleActions.setDuplicateGoogleForm({ duplicate: leadFormSetting.duplicateForm }));
          await dispatch(googleActions.setValidateGoogleLeadForm({ result: true, msg: "selected_form_have_used" }));
        }
        setStatusLoading(false);
      } else {
        const googleSheetsSetting = await dispatch(
          googleActions.setGoogleSheetsGGLeadsSetting({
            token: rmpToken,
            settingId:
              googleLeadFormSettingResult.result === 0 && googleLeadFormSettingResult.msg === "GET_DATA_COMPLETED"
                ? googleLeadFormSettingResult.data.id
                : leadFormSetting.result === 0
                ? leadFormSetting.msg == "UPDATE_DATA_COMPLETED"
                  ? leadFormSetting.data.data[0].id
                  : leadFormSetting.msg == "INSERT_DATA_COMPLETED"
                  ? leadFormSetting.data
                  : "0"
                : "0",
            googleAccount: params.googleAccount,
            spreadsheetUrl: params.spreadsheetUrl,
            refreshToken: googleUserInfoResult.result === 0 ? googleUserInfoResult.data.tokens.refresh_token : null,
          }),
        );

        if (googleSheetsSetting.result === 0 && googleSheetsSetting.msg != "NO_DATA") {
          setShowSuccessToast(true);
          setShow("hide");
          setStatusLoading(false);
          getAllLeadFormSettings("googleSheets");
          getGoogleAdsLeadForm("googleSheets");
          resetLeadFormSetting();
        } else {
          setShowFailedToast(true);
          setShow("hide");
          setStatusLoading(false);
          getAllLeadFormSettings("googleSheets");
          getGoogleAdsLeadForm("googleSheets");
          resetLeadFormSetting();
        }
      }
    } else {
      const googleSheetsSetting = await dispatch(
        googleActions.setGoogleSheetsGGLeadsSetting({
          token: rmpToken,
          settingId:
            googleLeadFormSettingResult.result === 0 && googleLeadFormSettingResult.msg === "GET_DATA_COMPLETED"
              ? googleLeadFormSettingResult.data.id
              : leadFormSetting.result === 0
              ? leadFormSetting.msg == "UPDATE_DATA_COMPLETED"
                ? leadFormSetting.data.data[0].id
                : leadFormSetting.msg == "INSERT_DATA_COMPLETED"
                ? leadFormSetting.data
                : "0"
              : "0",
          googleAccount: params.googleAccount,
          spreadsheetUrl: params.spreadsheetUrl,
          refreshToken: googleUserInfoResult.result === 0 ? googleUserInfoResult.data.tokens.refresh_token : null,
        }),
      );

      if (leadFormSetting.result === 0 || (googleSheetsSetting.result === 0 && googleSheetsSetting.msg != "NO_DATA")) {
        setShowSuccessToast(true);
        setShow("hide");
        setStatusLoading(false);
        getAllLeadFormSettings("googleSheets");
        getGoogleAdsLeadForm("googleSheets");
        resetLeadFormSetting();
      } else {
        setShowFailedToast(true);
        setShow("hide");
        setStatusLoading(false);
        getAllLeadFormSettings("googleSheets");
        getGoogleAdsLeadForm("googleSheets");
        resetLeadFormSetting();
      }
    }
  };

  /**
   * save connection (R-CRM tab)
   * @param {*} params
   */
  const setCRMLeadFormSetting = async (params) => {
    const leadFormSetting = await dispatch(
      googleActions.setGoogleAdsLeadFormSetting({
        token: rmpToken,
        appReserve: "rcrm",
        settingId:
          googleLeadFormSettingResult.result === 0 && googleLeadFormSettingResult.msg === "GET_DATA_COMPLETED"
            ? googleLeadFormSettingResult.data.id
            : "0",
        formSetting: params.formSetting,
        assetId: params.assetId,
      }),
    );

    /**
     * if duplicate form will show error in modal and can't save
     * if can't save facebook data (left side) will save only R-CRM data (right side)
     * if can't save R-CRM data (right side) will save only facebook data (left side)
     * if can't save both data will show failed toast
     */
    if (leadFormSetting.result !== 0) {
      /**save same form on same time */
      if (
        leadFormSetting.duplicateForm != undefined &&
        leadFormSetting.duplicateForm.length > 0 &&
        leadFormSetting.msg === "DUPLICATE_FORM_SELECTED"
      ) {
        if (params.formSetting == "all") {
          await dispatch(
            googleActions.getGoogleAdsLeadForm({
              token: rmpToken,
              appReserve: "rcrm",
            }),
          );
          await dispatch(googleActions.setDuplicateGoogleForm({ duplicate: "all" }));
          await dispatch(googleActions.setValidateGoogleLeadForm({ result: true, msg: "least_one_form" }));
        } else if (params.formSetting == "select") {
          const result = setCheckedGoogleAdsFormItemsResult.data.items.filter(
            (item) => !leadFormSetting.duplicateForm.includes(item),
          );
          await dispatch(
            googleActions.getGoogleAdsLeadForm({
              token: rmpToken,
              appReserve: "rcrm",
            }),
          );
          await dispatch(googleActions.setCheckedGoogleAdsFormItems(result));
          await dispatch(googleActions.setDuplicateGoogleForm({ duplicate: leadFormSetting.duplicateForm }));
          await dispatch(googleActions.setValidateGoogleLeadForm({ result: true, msg: "selected_form_have_used" }));
        }
        setStatusLoading(false);
      } else {
        const rcrmSetting = await dispatch(
          rcrmActions.saveCRMGGLeadsSetting({
            token: rmpToken,
            settingId:
              googleLeadFormSettingResult.result === 0 && googleLeadFormSettingResult.msg === "GET_DATA_COMPLETED"
                ? googleLeadFormSettingResult.data.id
                : leadFormSetting.result === 0
                ? leadFormSetting.msg == "UPDATE_DATA_COMPLETED"
                  ? leadFormSetting.data.data[0].id
                  : leadFormSetting.msg == "INSERT_DATA_COMPLETED"
                  ? leadFormSetting.data
                  : "0"
                : "0",
            targetType: "gate",
            gateId: rcrmGateItem.data.gateItem.gateId,
            gateName: rcrmGateItem.data.gateItem.gateName,
            userId: null,
            userEmail: null,
          }),
        );

        if (rcrmSetting.result === 0) {
          setShowSuccessToast(true);
          setStatusLoading(false);
          setShow("hide");
          getAllLeadFormSettings("rcrm");
          getGoogleAdsLeadForm("rcrm");
        } else {
          setShowFailedToast(true);
          setStatusLoading(false);
          setShow("hide");
          getAllLeadFormSettings("rcrm");
          getGoogleAdsLeadForm("rcrm");
        }
      }
    } else {
      const rcrmSetting = await dispatch(
        rcrmActions.saveCRMGGLeadsSetting({
          token: rmpToken,
          settingId:
            googleLeadFormSettingResult.result === 0 && googleLeadFormSettingResult.msg === "GET_DATA_COMPLETED"
              ? googleLeadFormSettingResult.data.id
              : leadFormSetting.result === 0
              ? leadFormSetting.msg == "UPDATE_DATA_COMPLETED"
                ? leadFormSetting.data.data[0].id
                : leadFormSetting.msg == "INSERT_DATA_COMPLETED"
                ? leadFormSetting.data
                : "0"
              : "0",
          targetType: "gate",
          gateId: rcrmGateItem.data.gateItem.gateId,
          gateName: rcrmGateItem.data.gateItem.gateName,
          userId: null,
          userEmail: null,
        }),
      );

      if (leadFormSetting.result === 0 || rcrmSetting.result === 0) {
        setShowSuccessToast(true);
        setStatusLoading(false);
        setShow("hide");
        getAllLeadFormSettings("rcrm");
        getGoogleAdsLeadForm("rcrm");
      } else {
        setShowFailedToast(true);
        setStatusLoading(false);
        setShow("hide");
        getAllLeadFormSettings("rcrm");
        getGoogleAdsLeadForm("rcrm");
      }
    }
  };

  /** action when click save button */
  const handleSaveSettingLeadForm = async () => {
    setStatusLoading(true);
    if (currentTab === "googleSheets") {
      await setGoogleAccount();
      const params = {
        formSetting: changeGoogleAdsFormSelectTypeResult.data.selectType,
        assetId:
          changeGoogleAdsFormSelectTypeResult.data.selectType === "all"
            ? "all"
            : setCheckedGoogleAdsFormItemsResult.data.items,
        googleAccount:
          googleUserInfoResult.result === 0
            ? googleUserInfoResult.data.googleAccount.google_account
            : googleSettingResult.result === 0 && googleSettingResult.msg === "GET_DATA_COMPLETED"
            ? googleSettingResult.data.google_account
            : null,
        spreadsheetUrl: changeSheetsUrlInputResult.data.sheetsUrl,
      };
      if (params.formSetting === "select" && params.assetId.length === 0) {
        await dispatch(googleActions.setValidateGoogleLeadForm({ result: true, msg: "least_one_form" }));
        setStatusLoading(false);
      } else if (params.formSetting === "" && params.assetId === "") {
        await dispatch(googleActions.setValidateGoogleLeadForm({ result: true, msg: "least_one_form" }));
        setStatusLoading(false);
      } else if (params.formSetting === null && params.assetId === null) {
        await dispatch(googleActions.setValidateGoogleLeadForm({ result: true, msg: "form_not_found" }));
        setStatusLoading(false);
      } else if (params.spreadsheetUrl == "" && params.googleAccount != null) {
        await dispatch(googleActions.setValidateGGLeadsGoogleSheetsUrl({ result: true, msg: "empty_url" }));
        setStatusLoading(false);
      } else if (Object.keys(checkGoogleSheetsError.data.validateSheets).length !== 0) {
        if (checkGoogleSheetsError.data.validateSheets.result === true) {
          await dispatch(
            googleActions.setValidateGGLeadsGoogleSheetsUrl({
              result: checkGoogleSheetsError.data.validateSheets.result,
              msg: checkGoogleSheetsError.data.validateSheets.msg,
            }),
          );
          setStatusLoading(false);
        } else {
          await setLeadFormSetting(params);
        }
      } else {
        await setLeadFormSetting(params);
      }
    } else if (currentTab === "rcrm") {
      const params = {
        formSetting: changeGoogleAdsFormSelectTypeResult.data.selectType,
        assetId:
          changeGoogleAdsFormSelectTypeResult.data.selectType === "all"
            ? "all"
            : setCheckedGoogleAdsFormItemsResult.data.items,
      };
      const target = await dispatch(
        rcrmActions.getLeadTarget({
          token: rmpToken,
        }),
      );
      if (params.formSetting === "select" && params.assetId.length === 0) {
        await dispatch(googleActions.setValidateGoogleLeadForm({ result: true, msg: "least_one_form" }));
        setStatusLoading(false);
      } else if (params.formSetting === "" && params.assetId === "") {
        await dispatch(googleActions.setValidateGoogleLeadForm({ result: true, msg: "least_one_form" }));
        setStatusLoading(false);
      } else if (params.formSetting === null && params.assetId === null) {
        await dispatch(googleActions.setValidateGoogleLeadForm({ result: true, msg: "form_not_found" }));
        setStatusLoading(false);
      } else if (
        target.result == 0 &&
        !target.gates.some((item) => item.gate_id === rcrmGateItem.data.gateItem.gateId)
      ) {
        await dispatch(rcrmActions.setValidateGate({ result: true, msg: "gate_not_found" }));
        setStatusLoading(false);
      } else {
        await setCRMLeadFormSetting(params);
      }
    }
  };

  const successToastCallback = (value) => {
    setShowSuccessToast(value);
  };

  const failedToastCallback = (value) => {
    setShowFailedToast(value);
  };

  const modalSettingContent = (
    <ModalContentContainer>
      <StyledContainer>
        <StyledCrossIcon onClick={statusLoading ? null : handleSettingCloseModal} disabled={statusLoading} />
        <SettingContainer windowSize={windowSize.width} tab={currentTab}>
          <SettingSubContainer tab={currentTab}>
            <SettingTitle>{t("__fbleads_google_sheets_tab_setting_modal")}</SettingTitle>
            {status === "add" && (
              <>
                <Row columnSpacing="0" responsive className="setting-row">
                  <Column className="left-column">
                    <IconArea>
                      <CircleIcon>
                        <GoogleAdsIcon src="/images/google-ads.png" />
                      </CircleIcon>
                    </IconArea>
                    <GoogleAdsSettingContainer>
                      <GoogleAdsSettingContent leadFormId={null} action={status} />
                    </GoogleAdsSettingContainer>
                  </Column>
                  <Column className="icon-arrow">
                    {windowSize.width <= 834 ? (
                      <Icon name="icon-down-dir" size="48px" color="#aaa" />
                    ) : (
                      <Icon name="icon-right-dir" size="48px" color="#aaa" />
                    )}
                  </Column>
                  <Column className="right-column">
                    {currentTab === "googleSheets" ? (
                      <>
                        <IconArea>
                          <CircleIcon>
                            <SheetIcon src="/images/sheets.png" />
                          </CircleIcon>
                        </IconArea>
                        <SheetSettingContainer>
                          <GoogleSettingContent leadFormId={null} />
                        </SheetSettingContainer>
                      </>
                    ) : (
                      currentTab === "rcrm" && (
                        <>
                          <IconArea>
                            <CircleCRMIcon>
                              <CRMIcon src="/images/r-crm-icon.png" />
                            </CircleCRMIcon>
                          </IconArea>
                          <CRMSettingContainer>
                            <CRMSettingContent />
                          </CRMSettingContainer>
                        </>
                      )
                    )}
                  </Column>

                  <ButtonContainer>
                    <>
                      <Button
                        type="secondary"
                        onClick={handleSettingCloseModal}
                        disabled={statusLoading ? true : false}
                        className={statusLoading ? "disabled-button" : ""}
                      >
                        {t("__cancel")}
                      </Button>
                      <Button
                        type="loading"
                        onClick={() => handleSaveSettingLeadForm()}
                        loading={statusLoading}
                        disabled={
                          setCheckedGoogleAdsFormItemsResult.type != undefined &&
                          setCheckedGoogleAdsFormItemsResult.data.items !== "" &&
                          changeGoogleAdsFormSelectTypeResult.type != undefined &&
                          changeGoogleAdsFormSelectTypeResult.data.selectType !== "" &&
                          !checkGSheetsDataLoadingStatus
                            ? false
                            : true
                        }
                        className={
                          setCheckedGoogleAdsFormItemsResult.type != undefined &&
                          setCheckedGoogleAdsFormItemsResult.data.items !== "" &&
                          changeGoogleAdsFormSelectTypeResult.type != undefined &&
                          changeGoogleAdsFormSelectTypeResult.data.selectType !== "" &&
                          !checkGSheetsDataLoadingStatus
                            ? ""
                            : "disabled-button"
                        }
                      >
                        {t("__save")}
                      </Button>
                    </>
                  </ButtonContainer>
                </Row>
              </>
            )}
            {status === "edit" &&
              (googleLeadFormSettingResult.result === 0 && googleLeadFormSettingResult.msg === "GET_DATA_COMPLETED" ? (
                <>
                  <Row columnSpacing="0" responsive className="setting-row">
                    <Column className="left-column">
                      <IconArea>
                        <CircleIcon>
                          <GoogleAdsIcon src="/images/google-ads.png" />
                        </CircleIcon>
                      </IconArea>
                      <GoogleAdsSettingContainer>
                        <GoogleAdsSettingContent leadFormId={googleLeadFormSettingResult.data.id} action={status} />
                      </GoogleAdsSettingContainer>
                    </Column>
                    <Column className="icon-arrow">
                      {windowSize.width <= 834 ? (
                        <Icon name="icon-down-dir" size="48px" color="#aaa" />
                      ) : (
                        <Icon name="icon-right-dir" size="48px" color="#aaa" />
                      )}
                    </Column>
                    <Column className="right-column">
                      {currentTab === "googleSheets" ? (
                        <>
                          <IconArea>
                            <CircleIcon>
                              <SheetIcon src="/images/sheets.png" />
                            </CircleIcon>
                          </IconArea>
                          <SheetSettingContainer>
                            <GoogleSettingContent leadFormId={googleLeadFormSettingResult.data.id} />
                          </SheetSettingContainer>
                        </>
                      ) : (
                        currentTab === "rcrm" && (
                          <>
                            <IconArea>
                              <CircleCRMIcon>
                                <CRMIcon src="/images/r-crm-icon.png" />
                              </CircleCRMIcon>
                            </IconArea>
                            <CRMSettingContainer>
                              <CRMSettingContent />
                            </CRMSettingContainer>
                          </>
                        )
                      )}
                    </Column>
                    <ButtonContainer>
                      <>
                        <Button
                          type="secondary"
                          onClick={handleSettingCloseModal}
                          disabled={statusLoading ? true : false}
                          className={statusLoading ? "disabled-button" : ""}
                        >
                          {t("__cancel")}
                        </Button>
                        <Button
                          type="loading"
                          onClick={() => handleSaveSettingLeadForm()}
                          loading={statusLoading}
                          disabled={
                            (currentTab === "googleSheets" && checkGSheetsDataLoadingStatus === true) ||
                            (currentTab === "rcrm" && checkRCRMDataLoadingStatus === true)
                              ? true
                              : false
                          }
                          className={
                            (currentTab === "googleSheets" && checkGSheetsDataLoadingStatus === true) ||
                            (currentTab === "rcrm" && checkRCRMDataLoadingStatus === true)
                              ? "disabled-button"
                              : ""
                          }
                        >
                          {t("__save")}
                        </Button>
                      </>
                    </ButtonContainer>
                  </Row>
                </>
              ) : (
                <LoadingArea>
                  <StyledLoadingIcon></StyledLoadingIcon>
                </LoadingArea>
              ))}
          </SettingSubContainer>
        </SettingContainer>
      </StyledContainer>
    </ModalContentContainer>
  );

  return (
    <>
      <Modal
        state={show}
        content={modalSettingContent}
        maxWidth={windowSize.width <= 834 ? windowSize.width : "900px"}
        custom={true}
      />
      <Toast
        show={showSuccessToast}
        message={t("__save_success")}
        bgColor="#4CAF50"
        callback={successToastCallback}
      ></Toast>
      <Toast
        show={showFailedToast}
        message={t("__save_failed")}
        bgColor="#F44336"
        callback={failedToastCallback}
      ></Toast>
    </>
  );
};

export default SettingModal;

const ModalContentContainer = styled.div`
  margin-bottom: 20px;
  font-family: "Poppins", "Kanit", "Helvetica Neue", sans-serif;
`;

const StyledContainer = styled.div`
  font-size: 14px;
  color: #555;
  position: relative;

  .m-b-10 {
    margin-bottom: 10px;
  }

  @media screen and (max-width: 834px) {
    .links-container {
      display: flex;
      justify-content: center;
    }
  }
`;

const StyledCrossIcon = styled(Cross).attrs((props) => ({
  disabled: props.disabled,
}))`
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 10px;
  width: 40px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? "0.5" : "1")};
`;

const SettingContainer = styled.div.attrs((props) => ({
  width: props.windowSize,
  height: props.tab === "googleSheets" ? "1055px" : "900px",
}))`
  display: flex;
  background-color: #fbfeff;
  border-radius: 10px;
  border: 1px solid #f5f9fc;
  padding: 20px;
  width: 900px;
  height: 600px;
  @media screen and (max-width: 834px) {
    width: ${(props) => props.width}px;
    height: ${(props) => props.height};
  }
`;

const SettingSubContainer = styled.div.attrs((props) => ({
  height: props.tab === "googleSheets" ? "360px" : "200px",
}))`
  line-height: 2.5em;
  width: 100%;

  .icon-row {
    display: flex;
    gap: 30px;
    height: 30px;
  }
  .left-icon-column {
    display: flex;
    justify-content: center;
    position: relative;
  }
  .right-icon-column {
    display: flex;
    justify-content: center;
    position: relative;
  }

  .setting-row {
    display: flex;
    gap: 0px;
  }
  .left-column {
    border: 1px solid #aaa;
    border-radius: 8px;
    height: 435px;
  }
  .icon-arrow {
    width: 60px;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 435px;
    @media screen and (max-width: 834px) {
      height: 100px;
      width: 100%;
      position: relative;
      top: -15px;
    }
  }
  .right-column {
    border: 1px solid #aaa;
    border-radius: 8px;
    height: 435px;
    @media screen and (max-width: 834px) {
      height: ${(props) => props.height};
    }
  }
`;

const SettingTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-align: left;
  color: #575757;
  margin-bottom: 30px;
`;
const IconArea = styled.div`
  width: 100%;
  position: relative;
  top: -30px;
  display: flex;
  justify-content: center;
`;

const CircleIcon = styled.div`
  width: 70px;
  height: 60px;
  border-radius: 50%;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CircleCRMIcon = styled.div`
  width: 90px;
  height: 60px;
  border-radius: 50%;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SheetIcon = styled.img`
  width: 60px;
  height: 60px;
`;

const GoogleAdsIcon = styled.img`
  width: 70px;
  height: 70px;
`;

const CRMIcon = styled.img`
  width: 60px;
  height: 60px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  position: absolute;
  bottom: 0px;
  right: 20px;
  margin-bottom: 20px;

  > Button {
    min-width: 100px;
    margin-bottom: 0px;
  }

  > Button:first-child {
    margin-right: 10px;
  }

  .disabled-button {
    cursor: not-allowed;
  }
`;

/**Facebook column**/
const GoogleAdsSettingContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 10px 20px 20px 20px;
  position: relative;
  top: -60px;
`;
/** Facebook Column */

/** Google Column */
const SheetSettingContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 10px 20px 20px 20px;
  position: relative;
  top: -60px;
`;
/** Google Column */

/** CRM Column */
const CRMSettingContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 10px 20px 20px 20px;
  position: relative;
  top: -60px;
`;
/** CRM Column */

const LoadingArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  width: 100%;
  height: 225px;
`;

const StyledLoadingIcon = styled.div`
  border: 5px solid #ccc;
  border-top: 5px solid #eee;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: button-keyframe-spin 1s linear infinite;
  box-sizing: initial !important;
  position: sticky;

  @keyframes button-keyframe-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
